import { __decorate } from "tslib";
import React, { Children, cloneElement, createElement, isValidElement, } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { action as mobxAction, computed, isArrayLike, observable, runInAction } from 'mobx';
import omit from 'lodash/omit';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import noop from 'lodash/noop';
import defaultTo from 'lodash/defaultTo';
import Responsive from '../../../lib/responsive/Responsive';
import { getConfig, getProPrefixCls } from '../../../lib/configure';
import { pxToRem } from '../../../lib/_util/UnitConvertor';
import isFunction from 'lodash/isFunction';
import isArray from 'lodash/isArray';
import axios from '../axios';
import autobind from '../_util/autobind';
import { getFieldsById } from '../field/FormField';
import FormContext from './FormContext';
import DataSetComponent from '../data-set/DataSetComponent';
import { LabelAlign, LabelLayout, ResponsiveKeys } from './enum';
import { defaultColumns, defaultExcludeUseColonTag, defaultLabelWidth, FIELD_SUFFIX, findFirstInvalidElement, getProperty, normalizeLabelWidth, } from './utils';
import FormVirtualGroup from './FormVirtualGroup';
/**
 * 表单name生成器
 */
const NameGen = (function* (start) {
    while (true) {
        start += 1;
        yield `form-${start}`;
    }
})(0);
const labelWidthPropTypes = PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.oneOf(['auto']),
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['auto'])])),
]);
const labelAlignPropTypes = PropTypes.oneOf([LabelAlign.left, LabelAlign.center, LabelAlign.right]);
const labelLayoutPropTypes = PropTypes.oneOf([
    LabelLayout.horizontal,
    LabelLayout.vertical,
    LabelLayout.placeholder,
    LabelLayout.float,
]);
let Form = class Form extends DataSetComponent {
    constructor(props, context) {
        super(props, context);
        this.fields = [];
        this.name = NameGen.next().value;
        runInAction(() => {
            this.responsiveItems = [];
        });
    }
    get axios() {
        return this.observableProps.axios || getConfig('axios') || axios;
    }
    get dataSet() {
        const { record } = this;
        if (record) {
            return record.dataSet;
        }
        return this.observableProps.dataSet;
    }
    get record() {
        const { record, dataSet, dataIndex } = this.observableProps;
        if (record) {
            return record;
        }
        if (dataSet) {
            if (isNumber(dataIndex)) {
                return dataSet.get(dataIndex);
            }
            return dataSet.current;
        }
        return undefined;
    }
    get dataIndex() {
        return this.observableProps.dataIndex;
    }
    get useColon() {
        const { useColon } = this.observableProps;
        if (useColon !== undefined) {
            return useColon;
        }
        const configUseColon = getConfig('useColon');
        if (configUseColon !== undefined) {
            return configUseColon;
        }
        return false;
    }
    get excludeUseColonTagList() {
        const { excludeUseColonTagList } = this.observableProps;
        if (excludeUseColonTagList !== undefined) {
            return excludeUseColonTagList;
        }
        const configExcludeUseColonTagList = getConfig('excludeUseColonTagList');
        if (configExcludeUseColonTagList !== undefined) {
            return configExcludeUseColonTagList;
        }
        return defaultExcludeUseColonTag;
    }
    get columns() {
        const { columns } = this.observableProps;
        if (isNumber(columns)) {
            return columns;
        }
        if (columns) {
            const responsiveColumns = this.responsiveItems[0];
            if (responsiveColumns) {
                return responsiveColumns;
            }
        }
        return defaultColumns;
    }
    get labelWidth() {
        const { labelWidth } = this.observableProps;
        if (labelWidth === 'auto') {
            return labelWidth;
        }
        if (isNumber(labelWidth) || isArrayLike(labelWidth)) {
            return labelWidth;
        }
        if (labelWidth) {
            const responsiveWidth = this.responsiveItems[1];
            if (responsiveWidth !== undefined) {
                return responsiveWidth;
            }
        }
        return defaultLabelWidth;
    }
    get labelAlign() {
        const { labelAlign } = this.observableProps;
        const defaultLabelAlign = this.labelLayout === LabelLayout.vertical ? LabelAlign.left : LabelAlign.right;
        if (isString(labelAlign)) {
            return labelAlign;
        }
        if (labelAlign) {
            const responsiveAlign = this.responsiveItems[2];
            if (responsiveAlign) {
                return responsiveAlign;
            }
        }
        return defaultLabelAlign;
    }
    get labelLayout() {
        const defaultLabelLayout = getConfig('labelLayout') || LabelLayout.horizontal;
        const { labelLayout } = this.observableProps;
        if (isString(labelLayout)) {
            return labelLayout;
        }
        if (labelLayout) {
            const responsiveLabelLayout = this.responsiveItems[3];
            if (responsiveLabelLayout) {
                return responsiveLabelLayout;
            }
        }
        return defaultLabelLayout;
    }
    get pristine() {
        return this.observableProps.pristine;
    }
    get separateSpacing() {
        const { separateSpacing } = this.observableProps;
        if (separateSpacing) {
            const { width = 0, height = 0 } = separateSpacing;
            if (width || height) {
                return {
                    width,
                    height,
                };
            }
        }
        return undefined;
    }
    isDisabled() {
        return super.isDisabled() || this.context.disabled;
    }
    isReadOnly() {
        return this.props.readOnly || this.context.readOnly;
    }
    getObservableProps(props, context) {
        return {
            ...super.getObservableProps(props, context),
            dataSet: 'dataSet' in props ? props.dataSet : context.dataSet,
            record: 'record' in props ? props.record : context.record,
            dataIndex: defaultTo(props.dataIndex, context.dataIndex),
            labelLayout: 'labelLayout' in props ? props.labelLayout : context.labelLayout,
            labelAlign: 'labelAlign' in props ? props.labelAlign : context.labelAlign,
            labelWidth: defaultTo(props.labelWidth, context.labelWidth),
            pristine: 'pristine' in props ? props.pristine : context.pristine,
            columns: props.columns,
            useColon: props.useColon,
            excludeUseColonTagList: props.excludeUseColonTagList,
            separateSpacing: props.separateSpacing,
        };
    }
    getOtherProps() {
        const otherProps = omit(super.getOtherProps(), [
            'record',
            'dataIndex',
            'onSuccess',
            'onError',
            'processParams',
            'labelWidth',
            'labelAlign',
            'labelLayout',
            'columns',
            'pristine',
            'axios',
            'useColon',
            'excludeUseColonTagList',
            'separateSpacing',
        ]);
        otherProps.onSubmit = this.handleSubmit;
        otherProps.onReset = this.handleReset;
        if (!otherProps.name) {
            otherProps.name = this.name;
        }
        return otherProps;
    }
    getHeader() {
        const { props: { header }, prefixCls, } = this;
        if (header) {
            return (React.createElement("div", { key: "form-header", className: `${prefixCls}-header` }, header));
        }
    }
    getClassName(...props) {
        const { prefixCls, labelLayout } = this;
        return super.getClassName({
            ...props,
            [`${prefixCls}-float-label`]: labelLayout === LabelLayout.float,
        });
    }
    componentWillMount() {
        this.processDataSetListener(true);
    }
    componentWillUnmount() {
        this.processDataSetListener(false);
    }
    processDataSetListener(flag) {
        const { dataSet } = this;
        if (dataSet) {
            const handler = flag ? dataSet.addEventListener : dataSet.removeEventListener;
            handler.call(dataSet, 'validate', this.handleDataSetValidate);
        }
    }
    // 处理校验失败定位
    async handleDataSetValidate({ result }) {
        if (!await result) {
            const item = this.element ? findFirstInvalidElement(this.element) : null;
            if (item) {
                item.focus();
            }
        }
    }
    rasterizedChildren() {
        const { dataSet, record, columns, labelLayout, labelAlign, useColon, excludeUseColonTagList, props: { children }, } = this;
        const prefixCls = getProPrefixCls(FIELD_SUFFIX);
        const labelWidth = normalizeLabelWidth(this.labelWidth, columns);
        const rows = [];
        let cols = [];
        let rowIndex = 0;
        let colIndex = 0;
        const matrix = [[]];
        let noLabel = true;
        const childrenArray = [];
        const separateSpacingWidth = this.separateSpacing ? this.separateSpacing.width / 2 : 0;
        Children.forEach(children, child => {
            if (isValidElement(child)) {
                const setChild = (arr, outChild, groupProps = {}) => {
                    if (noLabel === true &&
                        labelLayout === LabelLayout.horizontal &&
                        getProperty(outChild.props, 'label', dataSet, record)) {
                        noLabel = false;
                    }
                    if (!outChild?.type) {
                        return;
                    }
                    if (outChild?.type && outChild.type.displayName === 'FormVirtualGroup') {
                        if (!outChild.props.children) {
                            return;
                        }
                        if (isArray(outChild.props.children)) {
                            outChild.props.children.forEach((c) => {
                                setChild(arr, c, omit(outChild.props, ['children']));
                            });
                        }
                        else if (outChild?.type && outChild.type.displayName === 'FormVirtualGroup') {
                            setChild(arr, outChild.props.children, omit(outChild.props, ['children']));
                        }
                        else {
                            arr.push(cloneElement(outChild.props.children, {
                                ...groupProps,
                                ...outChild.props.children.props,
                            }));
                        }
                    }
                    else {
                        arr.push(cloneElement(outChild, {
                            ...groupProps,
                            ...outChild.props,
                        }));
                    }
                };
                setChild(childrenArray, child);
            }
        });
        function completeLine() {
            if (cols.length) {
                rows.push((React.createElement("tr", { key: `row-${rowIndex}` }, cols)));
                cols = [];
            }
            rowIndex++;
            colIndex = 0;
            matrix[rowIndex] = matrix[rowIndex] || [];
        }
        for (let index = 0, len = childrenArray.length; index < len;) {
            const { props, key, type } = childrenArray[index];
            let TagName = type;
            if (isFunction(type)) {
                TagName = type.displayName;
            }
            const label = getProperty(props, 'label', dataSet, record);
            const fieldLabelWidth = getProperty(props, 'labelWidth', dataSet, record);
            const required = getProperty(props, 'required', dataSet, record);
            const readOnly = getProperty(props, 'readOnly', dataSet, record) || this.isReadOnly();
            const { rowSpan = 1, colSpan = 1, newLine, className, fieldClassName, ...otherProps } = props;
            let newColSpan = colSpan;
            const currentRow = matrix[rowIndex];
            if (newLine) {
                if (colIndex !== 0) {
                    completeLine();
                    continue;
                }
            }
            while (currentRow[colIndex]) {
                colIndex++;
            }
            if (colIndex >= columns) {
                completeLine();
                continue;
            }
            if (newColSpan + colIndex > columns) {
                newColSpan = columns - colIndex;
            }
            for (let i = colIndex, k = colIndex + newColSpan; i < k; i++) {
                if (currentRow[i]) {
                    newColSpan = i - colIndex;
                    break;
                }
            }
            for (let i = rowIndex; i < rowSpan + rowIndex; i++) {
                for (let j = colIndex, k = newColSpan + colIndex; j < k; j++) {
                    if (!matrix[i]) {
                        matrix[i] = [];
                    }
                    matrix[i][j] = true;
                }
            }
            const isOutput = labelLayout === LabelLayout.horizontal && type.displayName === 'Output';
            const labelClassName = classNames(`${prefixCls}-label`, `${prefixCls}-label-${labelAlign}`, fieldClassName, {
                [`${prefixCls}-required`]: required,
                [`${prefixCls}-readonly`]: readOnly,
                [`${prefixCls}-label-vertical`]: labelLayout === LabelLayout.vertical,
                [`${prefixCls}-label-output`]: isOutput,
                [`${prefixCls}-label-useColon`]: useColon && !excludeUseColonTagList.find(v => v === TagName),
            });
            const wrapperClassName = classNames(`${prefixCls}-wrapper`, {
                [`${prefixCls}-output`]: isOutput,
            });
            if (!noLabel) {
                if (!isNaN(fieldLabelWidth)) {
                    labelWidth[colIndex] = Math.max(labelWidth[colIndex], fieldLabelWidth);
                }
                cols.push(React.createElement("td", { key: `row-${rowIndex}-col-${colIndex}-label`, className: labelClassName, rowSpan: rowSpan, style: this.labelLayout === LabelLayout.horizontal
                        && separateSpacingWidth
                        ? { paddingLeft: pxToRem(separateSpacingWidth + 5) } : undefined },
                    React.createElement("label", { title: isString(label) ? label : '' },
                        React.createElement("span", null, label))));
            }
            const fieldElementProps = {
                key,
                className: classNames(prefixCls, className),
                ...otherProps,
            };
            if (!isString(type)) {
                fieldElementProps.rowIndex = rowIndex;
                fieldElementProps.colIndex = colIndex;
            }
            cols.push(React.createElement("td", { key: `row-${rowIndex}-col-${colIndex}-field`, colSpan: noLabel ? newColSpan : newColSpan * 2 - 1, rowSpan: rowSpan, className: fieldClassName, style: this.labelLayout === LabelLayout.horizontal
                    && separateSpacingWidth
                    ? { paddingRight: pxToRem(separateSpacingWidth + 5) } : undefined },
                labelLayout === LabelLayout.vertical && (React.createElement("label", { className: labelClassName }, label)),
                React.createElement("div", { className: wrapperClassName }, createElement(type, fieldElementProps))));
            if (index === len - 1) {
                completeLine();
            }
            else {
                colIndex++;
            }
            index++;
        }
        cols = [];
        // 优化当使用separateSoacing label宽度太窄问题
        const labelWidthProcess = (widthInner) => {
            if (isNumber(widthInner)) {
                if (this.labelLayout === LabelLayout.horizontal) {
                    return separateSpacingWidth + widthInner;
                }
                return widthInner;
            }
            return separateSpacingWidth + defaultLabelWidth;
        };
        if (!noLabel) {
            for (let i = 0; i < columns; i++) {
                cols.push(React.createElement("col", { key: `label-${i}`, style: { width: pxToRem(labelWidthProcess(labelWidth[i % columns])) } }), React.createElement("col", { key: `wrapper-${i}` }));
            }
        }
        else {
            for (let i = 0; i < columns; i++) {
                cols.push(React.createElement("col", { key: `wrapper-${i}` }));
            }
        }
        let tableStyle;
        const { separateSpacing } = this;
        if (separateSpacing) {
            if (this.labelLayout === LabelLayout.horizontal) {
                tableStyle = {
                    borderCollapse: 'separate',
                    borderSpacing: `0rem ${pxToRem(separateSpacing.height)}`,
                };
            }
            else {
                tableStyle = {
                    borderCollapse: 'separate',
                    borderSpacing: `${pxToRem(separateSpacing.width)} ${pxToRem(separateSpacing.height)}`,
                };
            }
        }
        const isAutoWidth = this.labelWidth === 'auto' || (isArrayLike(this.labelWidth) && this.labelWidth.some(w => w === 'auto'));
        return [
            this.getHeader(),
            React.createElement("table", { style: tableStyle, key: "form-body", className: `${isAutoWidth ? 'auto-width' : ''}` },
                cols.length ? React.createElement("colgroup", null, cols) : undefined,
                React.createElement("tbody", null, rows)),
        ];
    }
    render() {
        const { labelWidth, labelAlign, labelLayout, pristine, dataSet, record, dataIndex, observableProps, } = this;
        const { formNode } = this.context;
        const value = {
            formNode: formNode || this,
            dataSet,
            dataIndex,
            record,
            labelWidth,
            labelAlign,
            labelLayout,
            pristine,
            disabled: this.isDisabled(),
            readOnly: this.isReadOnly(),
        };
        let children = this.rasterizedChildren();
        if (!formNode) {
            children = (React.createElement("form", Object.assign({}, this.getMergedProps(), { noValidate: true }), children));
        }
        return (React.createElement(Responsive, { items: [
                observableProps.columns,
                observableProps.labelWidth,
                observableProps.labelAlign,
                observableProps.labelLayout,
            ], onChange: this.handleResponsive },
            React.createElement(FormContext.Provider, { value: value }, children)));
    }
    handleResponsive(items) {
        this.responsiveItems = items;
    }
    async handleSubmit(e) {
        e.preventDefault();
        e.persist();
        if (await this.checkValidity()) {
            const { target, action, dataSet, method, processParams = noop, onSuccess = noop, onError = noop, onSubmit = noop, } = this.props;
            onSubmit(e);
            try {
                if (dataSet) {
                    onSuccess(await dataSet.submit());
                }
                else if (action) {
                    if (target && this.element) {
                        this.element.submit();
                    }
                    else {
                        onSuccess(await this.axios[method || 'get'](action, processParams(e)));
                    }
                }
            }
            catch (error) {
                onError(error);
            }
        }
    }
    handleReset(e) {
        const { onReset = noop } = this.props;
        const { record } = this;
        onReset(e);
        if (!e.isDefaultPrevented()) {
            if (record) {
                record.reset();
            }
            else {
                this.getFields().forEach(field => field.reset());
            }
        }
    }
    checkValidity() {
        const { dataSet } = this;
        if (dataSet) {
            if (!dataSet.length) {
                dataSet.create();
            }
            return dataSet.validate();
        }
        return Promise.all(this.getFields().map(field => field.checkValidity())).then(results => results.every(result => result));
    }
    getFields() {
        const { id } = this.props;
        if (id) {
            return [].concat(this.fields, getFieldsById(id));
        }
        return this.fields;
    }
    getField(name) {
        return this.getFields().find(field => field.props.name === name);
    }
    addField(field) {
        this.fields.push(field);
    }
    removeField(field) {
        const index = this.fields.indexOf(field);
        if (index !== -1) {
            this.fields.splice(index, 1);
        }
    }
};
Form.displayName = 'Form';
Form.FormVirtualGroup = FormVirtualGroup;
Form.propTypes = {
    /**
     * 表单提交请求地址
     */
    action: PropTypes.string,
    /**
     * 表单提交的HTTP Method
     * 可选值：POST | GET
     * @default POST
     */
    method: PropTypes.string,
    /**
     * 表单提交的目标
     * 当表单设置了设置target且没有dataSet时作浏览器默认提交，否则作Ajax提交
     */
    target: PropTypes.string,
    /**
     * Ajax提交时的参数回调
     */
    processParams: PropTypes.func,
    /**
     * 只读
     */
    readOnly: PropTypes.bool,
    /**
     * 内部控件的标签的宽度
     */
    labelWidth: PropTypes.oneOfType([
        labelWidthPropTypes,
        PropTypes.shape({
            [ResponsiveKeys.xs]: labelWidthPropTypes,
            [ResponsiveKeys.sm]: labelWidthPropTypes,
            [ResponsiveKeys.md]: labelWidthPropTypes,
            [ResponsiveKeys.lg]: labelWidthPropTypes,
            [ResponsiveKeys.xl]: labelWidthPropTypes,
            [ResponsiveKeys.xxl]: labelWidthPropTypes,
        }),
    ]),
    useColon: PropTypes.bool,
    excludeUseColonTagList: PropTypes.array,
    /**
     * 标签文字对齐方式
     * 可选值： 'left' | 'center' | 'right'
     */
    labelAlign: PropTypes.oneOfType([
        labelAlignPropTypes,
        PropTypes.shape({
            [ResponsiveKeys.xs]: labelAlignPropTypes,
            [ResponsiveKeys.sm]: labelAlignPropTypes,
            [ResponsiveKeys.md]: labelAlignPropTypes,
            [ResponsiveKeys.lg]: labelAlignPropTypes,
            [ResponsiveKeys.xl]: labelAlignPropTypes,
            [ResponsiveKeys.xxl]: labelAlignPropTypes,
        }),
    ]),
    /**
     * 标签位置
     * 可选值： 'horizontal' | 'vertical' | 'placeholder' | 'float' | 'none'
     */
    labelLayout: PropTypes.oneOfType([
        labelLayoutPropTypes,
        PropTypes.shape({
            [ResponsiveKeys.xs]: labelLayoutPropTypes,
            [ResponsiveKeys.sm]: labelLayoutPropTypes,
            [ResponsiveKeys.md]: labelLayoutPropTypes,
            [ResponsiveKeys.lg]: labelLayoutPropTypes,
            [ResponsiveKeys.xl]: labelLayoutPropTypes,
            [ResponsiveKeys.xxl]: labelLayoutPropTypes,
        }),
    ]),
    /**
     * 表单列数
     */
    columns: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.shape({
            [ResponsiveKeys.xs]: PropTypes.number,
            [ResponsiveKeys.sm]: PropTypes.number,
            [ResponsiveKeys.md]: PropTypes.number,
            [ResponsiveKeys.lg]: PropTypes.number,
            [ResponsiveKeys.xl]: PropTypes.number,
            [ResponsiveKeys.xxl]: PropTypes.number,
        }),
    ]),
    pristine: PropTypes.bool,
    /**
     * 表单头
     */
    header: PropTypes.string,
    /**
     * 提交回调
     */
    onSubmit: PropTypes.func,
    /**
     * 重置回调
     */
    onReset: PropTypes.func,
    /**
     * 提交成功回调
     */
    onSuccess: PropTypes.func,
    /**
     * 提交失败回调
     */
    onError: PropTypes.func,
    separateSpacing: PropTypes.object,
    ...DataSetComponent.propTypes,
};
Form.defaultProps = {
    suffixCls: 'form',
    columns: defaultColumns,
    labelWidth: defaultLabelWidth,
};
Form.contextType = FormContext;
__decorate([
    observable
], Form.prototype, "responsiveItems", void 0);
__decorate([
    computed
], Form.prototype, "axios", null);
__decorate([
    computed
], Form.prototype, "dataSet", null);
__decorate([
    computed
], Form.prototype, "record", null);
__decorate([
    computed
], Form.prototype, "dataIndex", null);
__decorate([
    computed
], Form.prototype, "useColon", null);
__decorate([
    computed
], Form.prototype, "excludeUseColonTagList", null);
__decorate([
    computed
], Form.prototype, "columns", null);
__decorate([
    computed
], Form.prototype, "labelWidth", null);
__decorate([
    computed
], Form.prototype, "labelAlign", null);
__decorate([
    computed
], Form.prototype, "labelLayout", null);
__decorate([
    computed
], Form.prototype, "pristine", null);
__decorate([
    computed
], Form.prototype, "separateSpacing", null);
__decorate([
    autobind
], Form.prototype, "handleDataSetValidate", null);
__decorate([
    autobind,
    mobxAction
], Form.prototype, "handleResponsive", null);
__decorate([
    autobind
], Form.prototype, "handleSubmit", null);
__decorate([
    autobind
], Form.prototype, "handleReset", null);
Form = __decorate([
    observer
], Form);
export default Form;
